/**
 * CLIENT REPORTS — what a client says about how their case was handled, and what
 * the PLATFORM decides to do about its own routing. Nothing else.
 *
 * ===========================================================================
 * READ THIS BEFORE ADDING A CODE, A STATUS, OR A LINE OF UI COPY
 * ===========================================================================
 *
 * In Sri Lanka an attorney-at-law's professional conduct is the business of the
 * Bar Association of Sri Lanka and the Supreme Court, which enrols attorneys and
 * alone may disenrol them. A platform that asserts "this attorney did not do
 * their work" or "this attorney behaved improperly" is (a) publishing a
 * defamatory statement of fact it cannot prove, and (b) doing the conduct
 * equivalent of what CLAUDE.md non-negotiable 2 forbids in the title domain —
 * rendering a professional conclusion that belongs to a qualified body, not to
 * us. The reasoning is identical in both cases and so is the rule.
 *
 * So this module can express exactly two kinds of fact, and no third:
 *
 *   (a) A CLIENT SAID X.  An allegation, always attributed to the client who
 *       made it, never restated by us as though it were established.
 *   (b) WE DECIDED Y ABOUT ROUTING WORK.  Our own commercial choice about
 *       whether to keep sending cases to this person ON THIS PLATFORM.
 *
 * There is no vocabulary here for "the professional did it", because no honest
 * one exists. `upheld` does NOT mean "the allegation is true" — see the comment
 * on CLIENT_REPORT_STATUSES, and use CLIENT_REPORT_STATUS_MEANINGS for any text
 * a human reads, so a screen cannot invent a stronger word than the data
 * supports.
 *
 * Two consequences that are not negotiable and are enforced elsewhere:
 *
 *   - NO AUTOMATIC ACTION ON A COUNT. There is deliberately no threshold
 *     constant in this file. N reports do not remove anyone. A human admin
 *     reads the history and decides; automating it would be the platform making
 *     a conduct determination by counter, which is the exact thing above.
 *   - REMOVAL IS THE EXISTING PATH, not a new one: revoking the professional's
 *     verification (decideVerification 'revoke'), with the new revocation reason
 *     WITHDRAWN_BY_PLATFORM in ./professional.ts. That makes them unassignable
 *     here and touches their enrolment not at all.
 */
/**
 * WHAT the client says went wrong — CODES, never free text, for three reasons
 * that all matter:
 *
 *   1. A code is impersonal. It names a category of complaint without narrating
 *      anything about a person, which is what lets an admin see a pattern
 *      without the platform ever having written a characterisation of anyone.
 *   2. A code is countable. "Three reports of UNRESPONSIVE" is a fact about our
 *      own records. "Three clients said he is negligent" is a fact we would have
 *      authored about him.
 *   3. Codes are the only thing that may reach the audit log, which is permanent
 *      and unerasable (COMPLIANCE Constraint 4). The free-text `detail` on the
 *      row is personal data about BOTH parties and must never go into it.
 *
 * Every code is phrased as the CLIENT'S EXPERIENCE, not as the professional's
 * act. `WORK_NOT_COMPLETED` is "the work I asked for has not arrived", which the
 * client can genuinely observe; it is not "the attorney neglected the file",
 * which they cannot, and which we must never say for them.
 */
export const CLIENT_REPORT_CATEGORIES = [
    /** The client says the verification work they asked for has not been delivered. */
    "WORK_NOT_COMPLETED",
    /** The client says they have not been able to get a reply. */
    "UNRESPONSIVE",
    /** The client says an arranged meeting did not happen. */
    "MISSED_APPOINTMENT",
    /** The client disputes what they were asked to pay, or what they were told it would be. */
    "FEE_DISPUTE",
    /**
     * A catch-all for concerns about how the client was treated that the codes
     * above do not fit. Deliberately vague, and deliberately NOT called
     * "MISCONDUCT": it records that the client raised a concern, and asserts
     * nothing whatever about whether anything improper occurred. Any finding of
     * professional misconduct is for the Bar Association and the Supreme Court.
     */
    "CONDUCT_CONCERN",
    /** None of the above. The client's own words go in `detail`, attributed to them. */
    "OTHER",
];
/**
 * Where the report has got to in OUR review. This is a status of the REPORT — a
 * record of what we did with it — not a verdict on a person.
 *
 * `upheld` is the word that will be misread, so it is defined here once and its
 * plain-English meaning is exported below for every screen to use verbatim:
 *
 *     UPHELD = "the platform accepts this report as a basis for ITS OWN
 *               decisions about routing work."
 *
 * It does NOT mean the allegation has been proved, that the professional did the
 * thing described, or that anyone has been found at fault. We are not competent
 * to decide any of those and must not appear to have decided them. An admin
 * reaches `upheld` when they judge the report to be a credible thing for US to
 * take into account when choosing whom to send cases to — a purely commercial
 * judgement about our own conduct, which is entirely ours to make.
 *
 * `dismissed` is the mirror image and is equally not a finding: it means we are
 * not taking this into account in our routing, and says nothing about whether
 * the underlying events happened.
 */
export const CLIENT_REPORT_STATUSES = [
    /** Filed by the client; no admin has reviewed it yet. The birth state. */
    "open",
    /** Reviewed: the platform will not act on this in its routing. Not a finding of innocence. */
    "dismissed",
    /** Reviewed: the platform accepts this as a basis for its OWN routing decisions. Not a finding of misconduct. */
    "upheld",
];
/**
 * The exact words to put on a screen, an email, or an export for each status.
 *
 * This exists because the risk in this whole feature is a UI author writing
 * "Complaint upheld" next to an attorney's name and meaning by it what the word
 * ordinarily means. The safe phrasing is not obvious and must not be re-derived
 * per screen, so it is data, exported once, and every surface reads it from
 * here. Same discipline as the vocabulary itself: one source, no drift.
 */
export const CLIENT_REPORT_STATUS_MEANINGS = {
    open: "Received. An administrator has not reviewed this yet.",
    dismissed: "Reviewed. The platform is not taking this report into account when deciding where to route work. This is not a finding about the professional either way.",
    upheld: "Reviewed. The platform accepts this report as a basis for its own decisions about routing work. This is not a finding of professional misconduct — an attorney-at-law's conduct is a matter for the Bar Association of Sri Lanka and the Supreme Court.",
};
/**
 * The legal edges. Written out rather than inferred, and the absent ones are the
 * point — the same discipline as the case machine (./workflow.ts) and the
 * verification machine (./professional.ts).
 *
 *   - A report is BORN `open` and reaches a decision exactly once. Both
 *     decisions are TERMINAL: an admin cannot quietly re-open a dismissed report
 *     and re-decide it, and cannot walk `upheld` back to `dismissed` after a
 *     routing decision has been taken on the strength of it. If the platform
 *     changes its mind, that is a NEW decision with its own record, exactly as
 *     an assignment is revoked rather than rewritten.
 *   - There is no `withdrawn`, and no path back to `open`. A client who no
 *     longer wants to pursue a report has said something we recorded; deleting
 *     or reversing the record would leave a routing decision we already made
 *     with nothing behind it.
 *
 * The API and migration 0021's trigger read THIS table of edges — the trigger
 * duplicates it in SQL deliberately, for the reason 0009 gives: the shared graph
 * gives an early typed error and drives the UI, the trigger is the guarantee
 * that no path escapes it, including raw SQL as titleverify_app.
 */
const CLIENT_REPORT_TRANSITIONS = {
    open: ["dismissed", "upheld"],
    dismissed: [],
    upheld: [],
};
export function canTransitionClientReport(from, to) {
    return CLIENT_REPORT_TRANSITIONS[from].includes(to);
}
export function allowedClientReportTransitions(from) {
    return CLIENT_REPORT_TRANSITIONS[from];
}
export function isTerminalClientReportStatus(status) {
    return CLIENT_REPORT_TRANSITIONS[status].length === 0;
}
/** A report an admin has decided on. Its reviewer and review time are never null. */
export function isReviewedClientReportStatus(status) {
    return status !== "open";
}
//# sourceMappingURL=client-report.js.map